import { useCallback, useEffect, useState } from "react";
import { FiEdit2, FiInfo } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import Button from "components/Button";
import FormTextInput from "components/form/FormTextInput";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import swtAlert from "lib/sweetAlert";
import {
  validateName,
  validatePhone,
  validateForm as runFormValidation,
} from "lib/validation";
import { selectAuthUser } from "store/reducer";
import { getProfileNames, refreshAuthUser } from "pages/settings/settingsHelper";

function buildFormFromUser(user) {
  const { firstName, lastName } = getProfileNames(user);
  return {
    firstName,
    lastName,
    phone: String(user?.phone ?? "").replace(/\D/g, ""),
  };
}

export default function SettingsProfilePage() {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);

  const [formData, setFormData] = useState(() => buildFormFromUser(user));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const applyUserToForm = useCallback(() => {
    setFormData(buildFormFromUser(user));
    setErrors({});
  }, [user]);

  useEffect(() => {
    applyUserToForm();
  }, [applyUserToForm]);

  const clearError = useCallback((field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));
  }, []);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (name === "phone") {
        setFormData((prev) => ({ ...prev, phone: value.replace(/\D/g, "") }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      clearError(name);
    },
    [clearError]
  );

  const validateForm = useCallback(() => {
    const newErrors = runFormValidation(formData, {
      firstName: (v) => validateName(v, { label: "First name" }),
      lastName: (v) => validateName(v, { label: "Last name" }),
      phone: (v) => validatePhone(v),
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleCancelEdit = useCallback(() => {
    applyUserToForm();
    setIsEditing(false);
  }, [applyUserToForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing) return;
    if (!validateForm()) {
      await swtAlert.warning(
        "Check your details",
        "Please fix the highlighted fields before saving."
      );
      return;
    }

    const firstName = formData.firstName.trim();
    const lastName = formData.lastName.trim();

    setSaving(true);
    try {
      const resp = await api.patch(API_ENDPOINTS.profile, {
        firstName,
        lastName,
        phone: formData.phone.trim(),
      });
      if (!isSuccessResponse(resp)) {
        await swtAlert.error(
          "Update failed",
          resp?.message || "Could not update your profile."
        );
        return;
      }
      await refreshAuthUser(dispatch);
      setIsEditing(false);
      await swtAlert.success(
        "Profile updated",
        resp?.message || "Your account details have been saved."
      );
    } catch (err) {
      await swtAlert.error(
        "Update failed",
        err?.message || "Could not update your profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldDisabled = !isEditing;

  return (
    <div className="max-w-xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-text-900 md:text-2xl">
              Account Information
            </h1>
            {isEditing ? null : (
              <button
                type="button"
                className="rounded-lg p-2 text-text-500 transition hover:bg-slate-100 hover:text-brand-900"
                onClick={() => setIsEditing(true)}
                aria-label="Edit profile"
              >
                <FiEdit2 size={18} />
              </button>
            )}
          </div>
          <div className="mt-2 h-0.5 w-full bg-brand-900" aria-hidden />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-text-700">
        <FiInfo className="mt-0.5 shrink-0 text-brand-900" aria-hidden />
        <p>Update your first name, last name, and phone number. Email cannot be changed here.</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          <FormTextInput
            label="First Name"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleInputChange}
            disabled={fieldDisabled}
            error={errors.firstName}
          />

          <FormTextInput
            label="Last Name"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleInputChange}
            disabled={fieldDisabled}
            error={errors.lastName}
          />
        </div>

        <FormTextInput
          label="Email"
          name="email"
          value={user?.email || ""}
          disabled
          inputClassName="bg-slate-50"
        />

        <FormTextInput
          label="Phone Number"
          name="phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder="6042224444"
          value={formData.phone}
          onChange={handleInputChange}
          disabled={fieldDisabled}
          error={errors.phone}
        />

        {isEditing ? (
          <div className="flex flex-wrap gap-3 pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? "Updating…" : "Update Profile"}
            </Button>
            <Button type="button" variant="secondary" disabled={saving} onClick={handleCancelEdit}>
              Cancel
            </Button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
